import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getClients, getInvoice, updateInvoice } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import PaymentTermsField from '../components/PaymentTermsField';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  CalendarIcon,
  Loader2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const EditInvoicePage = () => {
  const { id } = useParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [clientId, setClientId] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentMethods, setPaymentMethods] = useState(['virement']);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([
    { description: '', quantity: 1, unit_price: 0, vat_rate: 7.7 }
  ]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clientsRes, invoiceRes] = await Promise.all([
        getClients(),
        getInvoice(id)
      ]);
      setClients(clientsRes.data);
      const inv = invoiceRes.data;
      setInvoice(inv);
      setClientId(inv.client_id);
      setItems(inv.items);
      setPaymentTerms(inv.payment_terms || '');
      setPaymentMethods(inv.accepted_payment_methods || ['virement']);
      setNotes(inv.notes || '');
      setStatus(inv.status);
      if (inv.due_date) {
        setDueDate(parseISO(inv.due_date));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, vat_rate: 7.7 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setItems(newItems);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateVat = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price * item.vat_rate / 100), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVat();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(amount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!clientId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    if (items.some(item => !item.description || item.quantity <= 0)) {
      toast.error('Veuillez remplir tous les articles');
      return;
    }

    setSaving(true);
    try {
      await updateInvoice(id, {
        client_id: clientId,
        items,
        status,
        payment_terms: paymentTerms || null,
        accepted_payment_methods: paymentMethods.length > 0 ? paymentMethods : null,
        notes: notes || null,
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null
      });
      toast.success('Facture mise à jour');
      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="edit-invoice-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/invoices')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Modifier la facture
          </h1>
          <p className="text-slate-500">
            {invoice?.invoice_number}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Selection */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Client</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger data-testid="client-select">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} - {client.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Items */}
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">Articles</CardTitle>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addItem}
                  data-testid="add-item-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div 
                      key={index} 
                      className="grid grid-cols-12 gap-3 items-start p-4 bg-slate-50 rounded-lg"
                      data-testid={`invoice-item-${index}`}
                    >
                      <div className="col-span-12 sm:col-span-5">
                        <Label className="text-xs text-slate-500">Description</Label>
                        <Input
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Description de l'article"
                          data-testid={`item-description-${index}`}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-xs text-slate-500">Quantité</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          data-testid={`item-quantity-${index}`}
                        />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-xs text-slate-500">Prix unitaire</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          data-testid={`item-price-${index}`}
                        />
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        <Label className="text-xs text-slate-500">TVA %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={item.vat_rate}
                          onChange={(e) => updateItem(index, 'vat_rate', e.target.value)}
                          data-testid={`item-vat-${index}`}
                        />
                      </div>
                      <div className="col-span-1 flex items-end pb-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Notes additionnelles</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Notes ou commentaires pour cette facture..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid="notes-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Statut</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="paid">Payée</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Due Date */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Échéance</CardTitle>
              </CardHeader>
              <CardContent>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      data-testid="due-date-btn"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {dueDate ? format(dueDate, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </CardContent>
            </Card>

            {/* Payment Terms */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Conditions de paiement</CardTitle>
              </CardHeader>
              <CardContent>
                <PaymentTermsField
                  value={paymentTerms}
                  onChange={setPaymentTerms}
                  methods={paymentMethods}
                  onMethodsChange={setPaymentMethods}
                />
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="border-slate-200 bg-slate-50">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Sous-total HT</span>
                  <span className="font-medium" data-testid="subtotal">{formatCurrency(calculateSubtotal())}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">TVA</span>
                  <span className="font-medium" data-testid="vat-total">{formatCurrency(calculateVat())}</span>
                </div>
                <div className="border-t border-slate-200 pt-3 flex justify-between">
                  <span className="font-semibold text-slate-900">Total TTC</span>
                  <span className="font-bold text-lg text-blue-600" data-testid="total">{formatCurrency(calculateTotal())}</span>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={saving || !clientId}
              data-testid="save-invoice-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditInvoicePage;
